import { Alert, Button, Card, SelectField, TextAreaField, TextField, useToast } from '@cartly/ui';
import { Plus, Send, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api } from '../../api/client';
import { useSubmit } from '../../app/hooks';

interface AccountChoice {
  id: string;
  name: string;
  wabaId: string;
}

type ButtonDraft =
  | { type: 'QUICK_REPLY'; text: string }
  | { type: 'URL'; text: string; url: string; example: string }
  | { type: 'PHONE_NUMBER'; text: string; phone: string };

const LANGUAGES = [
  { value: 'en', label: 'English (en)' },
  { value: 'en_US', label: 'English US (en_US)' },
  { value: 'en_GB', label: 'English UK (en_GB)' },
  { value: 'hi', label: 'Hindi (hi)' },
  { value: 'ta', label: 'Tamil (ta)' },
  { value: 'te', label: 'Telugu (te)' },
  { value: 'ml', label: 'Malayalam (ml)' },
  { value: 'kn', label: 'Kannada (kn)' },
  { value: 'mr', label: 'Marathi (mr)' },
  { value: 'gu', label: 'Gujarati (gu)' },
  { value: 'bn', label: 'Bengali (bn)' },
];

/** Number of distinct `{{n}}` variables in a text. */
function countVars(text: string): number {
  return new Set(text.match(/\{\{\s*\d+\s*\}\}/g) ?? []).size;
}

const NEW_BUTTON: Record<ButtonDraft['type'], ButtonDraft> = {
  QUICK_REPLY: { type: 'QUICK_REPLY', text: '' },
  URL: { type: 'URL', text: '', url: '', example: '' },
  PHONE_NUMBER: { type: 'PHONE_NUMBER', text: '', phone: '' },
};

export interface EditableTemplate {
  id: string;
  name: string;
  language: string;
  category: string;
  components: unknown;
}

interface Component {
  type?: string;
  format?: string;
  text?: string;
  example?: { header_text?: string[]; body_text?: string[][] };
  buttons?: Array<{
    type?: string;
    text?: string;
    url?: string;
    phone_number?: string;
    example?: string[];
  }>;
}

/** True when the form can represent this template (text header only, supported buttons). */
export function isFormEditable(components: unknown): boolean {
  const list = (Array.isArray(components) ? components : []) as Component[];
  return list.every((c) => {
    if (c.type === 'HEADER') return c.format === 'TEXT';
    if (c.type === 'BUTTONS') {
      return (c.buttons ?? []).every(
        (b) => b.type === 'QUICK_REPLY' || b.type === 'URL' || b.type === 'PHONE_NUMBER',
      );
    }
    return c.type === 'BODY' || c.type === 'FOOTER';
  });
}

/** Form values for an existing template (or blanks for a new one). */
function draftFrom(editing?: EditableTemplate | null) {
  const list = (Array.isArray(editing?.components) ? editing.components : []) as Component[];
  const h = list.find((c) => c.type === 'HEADER');
  const b = list.find((c) => c.type === 'BODY');
  const f = list.find((c) => c.type === 'FOOTER');
  const btns = list.find((c) => c.type === 'BUTTONS')?.buttons ?? [];
  return {
    name: editing?.name ?? '',
    language: editing?.language ?? 'en',
    category: (editing?.category === 'UTILITY' ? 'UTILITY' : 'MARKETING') as
      'MARKETING' | 'UTILITY',
    header: h?.text ?? '',
    headerExample: h?.example?.header_text?.[0] ?? '',
    body: b?.text ?? '',
    examples: b?.example?.body_text?.[0] ?? [],
    footer: f?.text ?? '',
    buttons: btns.map((x): ButtonDraft =>
      x.type === 'URL'
        ? { type: 'URL', text: x.text ?? '', url: x.url ?? '', example: x.example?.[0] ?? '' }
        : x.type === 'PHONE_NUMBER'
          ? { type: 'PHONE_NUMBER', text: x.text ?? '', phone: x.phone_number ?? '' }
          : { type: 'QUICK_REPLY', text: x.text ?? '' },
    ),
  };
}

/** Creates a message template, or edits an existing one, and submits it to Meta for review. */
export function TemplateCreate({
  accounts,
  onCreated,
  editing,
  onCancelEdit,
}: {
  accounts: AccountChoice[];
  onCreated: () => void;
  editing?: EditableTemplate | null;
  onCancelEdit?: () => void;
}) {
  const toast = useToast();
  const submit = useSubmit();
  const [open, setOpen] = useState(false);
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? '');
  const init = draftFrom(editing);
  const [name, setName] = useState(init.name);
  const [language, setLanguage] = useState(init.language);
  const [category, setCategory] = useState<'MARKETING' | 'UTILITY'>(init.category);
  const [header, setHeader] = useState(init.header);
  const [headerExample, setHeaderExample] = useState(init.headerExample);
  const [body, setBody] = useState(init.body);
  const [examples, setExamples] = useState<string[]>(init.examples);
  const [footer, setFooter] = useState(init.footer);
  const [buttons, setButtons] = useState<ButtonDraft[]>(init.buttons);

  const bodyVars = countVars(body);
  const headerHasVar = countVars(header) > 0;

  const reset = () => {
    setName('');
    setHeader('');
    setHeaderExample('');
    setBody('');
    setExamples([]);
    setFooter('');
    setButtons([]);
  };

  const updateButton = (i: number, patch: Partial<ButtonDraft>) =>
    setButtons((list) => list.map((b, j) => (j === i ? ({ ...b, ...patch } as ButtonDraft) : b)));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      name,
      language,
      category,
      ...(header.trim() ? { header: header.trim() } : {}),
      ...(headerHasVar ? { headerExample } : {}),
      body,
      bodyExamples: Array.from({ length: bodyVars }, (_, i) => examples[i] ?? ''),
      ...(footer.trim() ? { footer: footer.trim() } : {}),
      buttons: buttons.map((b) =>
        b.type === 'URL' && !b.url.endsWith('{{1}}')
          ? { type: b.type, text: b.text, url: b.url }
          : b,
      ),
    };
    const ok = await submit.run(async () => {
      if (editing) await api.put(`/whatsapp/templates/${editing.id}`, payload);
      else await api.post(`/whatsapp/accounts/${accountId}/templates`, payload);
      return true;
    });
    if (ok) {
      toast.success(
        editing ? 'Changes submitted to Meta for review' : 'Template submitted to Meta for review',
      );
      reset();
      setOpen(false);
      onCreated();
      onCancelEdit?.();
    }
  };

  if (accounts.length === 0 && !editing) return null;
  if (!open && !editing) {
    return (
      <div>
        <Button variant="primary" icon={<Plus size={14} />} onClick={() => setOpen(true)}>
          New template
        </Button>
      </div>
    );
  }

  return (
    <Card
      title={editing ? `Edit ${editing.name}` : 'Create a template'}
      subtitle={
        editing
          ? 'Saving sends the new content to Meta for review again. The name and language cannot be changed.'
          : 'Meta reviews every template, usually within minutes. Once approved it appears below and can be used in messages, campaigns and flows.'
      }
    >
      <form className="stack" onSubmit={(e) => void onSubmit(e)}>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        {!editing && accounts.length > 1 && (
          <SelectField
            label="WhatsApp account"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            options={accounts.map((a) => ({ value: a.id, label: `${a.name} (${a.wabaId})` }))}
          />
        )}
        <TextField
          label="Template name"
          value={name}
          onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
          hint="Lowercase letters, numbers and underscores, e.g. abandoned_cart_01. Cannot be changed later."
          maxLength={512}
          disabled={!!editing}
          required
        />
        <div className="form-grid">
          <SelectField
            label="Language"
            disabled={!!editing}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            options={LANGUAGES}
          />
          <SelectField
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value as 'MARKETING' | 'UTILITY')}
            options={[
              { value: 'MARKETING', label: 'Marketing (offers, cart reminders)' },
              { value: 'UTILITY', label: 'Utility (order updates, confirmations)' },
            ]}
            hint="Meta may re-categorise a template it considers promotional."
          />
        </div>
        <TextField
          label="Header (optional)"
          value={header}
          onChange={(e) => setHeader(e.target.value)}
          hint="Plain text, up to 60 characters. At most one variable, {{1}}."
          maxLength={60}
        />
        {headerHasVar && (
          <TextField
            label="Header sample for {{1}}"
            value={headerExample}
            onChange={(e) => setHeaderExample(e.target.value)}
            required
          />
        )}
        <TextAreaField
          label="Body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          hint="Up to 1024 characters. Use {{1}}, {{2}}… for variables such as the customer's name. A variable cannot be the very first or last thing in the body."
          maxLength={1024}
          rows={6}
          required
        />
        {Array.from({ length: bodyVars }, (_, i) => (
          <TextField
            key={i}
            label={`Sample for {{${i + 1}}}`}
            value={examples[i] ?? ''}
            onChange={(e) => {
              const next = [...examples];
              next[i] = e.target.value;
              setExamples(next);
            }}
            hint={i === 0 ? 'Meta asks for a realistic example of each variable.' : undefined}
            required
          />
        ))}
        <TextField
          label="Footer (optional)"
          value={footer}
          onChange={(e) => setFooter(e.target.value)}
          hint="Up to 60 characters, no variables."
          maxLength={60}
        />

        {buttons.map((b, i) => (
          <div key={i} className="stack template-button">
            <div className="row-between">
              <strong>
                Button {i + 1}:{' '}
                {b.type === 'URL' ? 'Link' : b.type === 'PHONE_NUMBER' ? 'Call' : 'Quick reply'}
              </strong>
              <Button
                size="sm"
                variant="ghost"
                icon={<Trash2 size={14} />}
                onClick={() => setButtons((list) => list.filter((_, j) => j !== i))}
              >
                Remove
              </Button>
            </div>
            <TextField
              label="Button text"
              value={b.text}
              onChange={(e) => updateButton(i, { text: e.target.value })}
              maxLength={25}
              required
            />
            {b.type === 'URL' && (
              <>
                <TextField
                  label="Link"
                  value={b.url}
                  onChange={(e) => updateButton(i, { url: e.target.value })}
                  hint="Fixed link, or end it with {{1}} to fill the last part per customer, e.g. https://crm.cartly360.com/r/{{1}}."
                  required
                />
                {b.url.endsWith('{{1}}') && (
                  <TextField
                    label="Sample full link"
                    value={b.example}
                    onChange={(e) => updateButton(i, { example: e.target.value })}
                    hint="A complete example, e.g. https://crm.cartly360.com/r/aB3dE9fGh2k."
                    required
                  />
                )}
              </>
            )}
            {b.type === 'PHONE_NUMBER' && (
              <TextField
                label="Phone number"
                value={b.phone}
                onChange={(e) => updateButton(i, { phone: e.target.value })}
                hint="With country code, e.g. +919876543210."
                inputMode="tel"
                required
              />
            )}
          </div>
        ))}
        {buttons.length < 3 && (
          <div className="row">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setButtons((l) => [...l, NEW_BUTTON.URL])}
            >
              + Link button
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setButtons((l) => [...l, NEW_BUTTON.QUICK_REPLY])}
            >
              + Quick reply
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setButtons((l) => [...l, NEW_BUTTON.PHONE_NUMBER])}
            >
              + Call button
            </Button>
          </div>
        )}

        <div className="row">
          <Button
            type="submit"
            variant="primary"
            icon={<Send size={14} />}
            loading={submit.pending}
            disabled={!editing && !accountId}
          >
            {editing ? 'Save and resubmit' : 'Submit to Meta'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              reset();
              setOpen(false);
              onCancelEdit?.();
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
